import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";

export default function MoatTechEdge() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8">
      <SlideHeader eyebrow="Moat">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          The gap widens every week.
        </h1>
      </SlideHeader>

      <div className="grid grid-cols-1 gap-8 md:flex-1 md:grid-cols-2 md:gap-12">
        <MoatColumn
          number="1"
          label="ASO Methodology"
          subtitle="Human-authored, weekly-updated"
        >
          <p className="text-base leading-relaxed">
            Nine specialized ASO modules, each encoding senior ASO judgment. Updated{" "}
            <span className="font-semibold text-slate-900">every week</span> by
            named human ASO practitioners as Apple and Google ship algorithm
            changes, the LLM-discovery landscape shifts, and new patterns
            emerge.
          </p>
          <p className="mt-4 text-base leading-relaxed">
            100+ expert-crafted prompts per audit. A four-pillar scoring
            rubric. A structured rubric for cross-validating outputs across
            multiple frontier models.
          </p>
          <p className="mt-4 text-lg font-semibold text-slate-900">
            The recipe compounds. LLMs don't.
          </p>
        </MoatColumn>
        <MoatColumn
          number="2"
          label="Production Infrastructure"
          subtitle="Not a thin wrapper"
        >
          <p className="text-sm leading-relaxed text-slate-700">
            What it takes to run that methodology daily across every app in a
            customer's portfolio:
          </p>
          <ul className="mt-2.5 space-y-1.5 text-sm leading-snug text-slate-700">
            <InfraItem>
              100+ expert prompts in a tested prompt catalog
            </InfraItem>
            <InfraItem>
              Multi-provider AI cross-validation (OpenAI + Anthropic + Google
              + Perplexity)
            </InfraItem>
            <InfraItem>
              Live App Store + Google Play scrapers, per locale, per region
            </InfraItem>
            <InfraItem>
              App Store Connect + Play Console OAuth + ingestion pipelines
            </InfraItem>
            <InfraItem>
              Reddit, web, and LLM-surface probing — geo-distributed
            </InfraItem>
            <InfraItem>
              Proxy infrastructure for global, unthrottled crawls
            </InfraItem>
            <InfraItem>
              Multi-layer caching that compresses inference cost 10–50×
            </InfraItem>
            <InfraItem>
              Daily change detection across all four surfaces
            </InfraItem>
            <InfraItem>
              Orchestration: four streams → Weekly Revision Plan
            </InfraItem>
          </ul>
          <p className="mt-3 text-sm italic leading-snug text-slate-500">
            Thirty years of building products at scale — Netflix, Google,
            MessageBird, Welltech, four patents.
          </p>
        </MoatColumn>
      </div>

      <div className="mt-4 rounded-lg bg-blue-50 p-3 ring-1 ring-blue-200 md:mt-5 md:p-4">
        <p className="text-sm leading-snug text-blue-900 md:text-base">
          Both moats compound weekly. The customer sees the compounding on
          their own apps —{" "}
          <span className="font-semibold">
            rising visibility, rising conversion, rising organic installs.
          </span>
        </p>
      </div>

      <div className="mt-4 border-t border-slate-200 pt-4 md:mt-5">
        <p className="text-sm leading-relaxed text-slate-700 md:text-base">
          <span className="font-semibold text-slate-900">
            Won't OpenAI / Anthropic / Google launch this?
          </span>{" "}
          They sell horsepower. We sell the methodology that runs on the
          horsepower — and the production infrastructure that scales it across
          portfolios with reliability, cost discipline, and live store
          integration.{" "}
          <span className="italic text-slate-600">
            They're one input. We ship the system.
          </span>
        </p>
      </div>
    </div>
  );
}

function MoatColumn({
  number,
  label,
  subtitle,
  children,
}: {
  number: string;
  label: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col">
      <div className="flex items-baseline gap-3">
        <span className="text-3xl font-bold text-blue-700">{number}</span>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">
            {label}
          </p>
          <p className="text-base text-slate-600">{subtitle}</p>
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function InfraItem({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-2">
      <span className="text-blue-600">•</span>
      <span>{children}</span>
    </li>
  );
}
